import { candidateDomainKey } from '@/lib/competition/candidate-domain'
import type {
  BrandCandidate,
  CompetitorCandidate,
  CompetitorCandidateGroup,
} from '@/types/competition'

/** A discovery's companies split by group, each in list order. */
export type CandidateGroups = Readonly<
  Record<CompetitorCandidateGroup, readonly CompetitorCandidate[]>
>

/** The domain keys a fresh list starts with ticked: the first `limit` direct companies. */
export function defaultPicks(
  candidates: readonly CompetitorCandidate[],
  limit: number,
): ReadonlySet<string> {
  return new Set(
    candidates
      .filter((candidate) => candidate.group === 'direct')
      .slice(0, limit)
      .map((candidate) => candidateDomainKey(candidate.domain)),
  )
}

function directRow(candidate: BrandCandidate): CompetitorCandidate {
  return {
    name: candidate.name,
    domain: candidate.domain,
    websiteUrl: candidate.websiteUrl,
    lists: [],
    group: 'direct',
    reason: null,
    searchOverlap: null,
    namedOverlap: [],
  }
}

/** The list with a company added at the start of the direct group. */
export function withDirectCandidate(
  candidates: readonly CompetitorCandidate[],
  candidate: BrandCandidate,
): CompetitorCandidate[] {
  const at = candidates.findIndex((kept) => kept.group === 'direct')
  const from = Math.max(at, 0)
  return [...candidates.slice(0, from), directRow(candidate), ...candidates.slice(from)]
}

function withTrailingDirectCandidate(
  candidates: readonly CompetitorCandidate[],
  candidate: BrandCandidate,
): CompetitorCandidate[] {
  const at = candidates.reduce((end, kept, index) => (kept.group === 'direct' ? index + 1 : end), 0)
  return [...candidates.slice(0, at), directRow(candidate), ...candidates.slice(at)]
}

/** Splits the companies by group, keeping list order. */
export function groupCandidates(candidates: readonly CompetitorCandidate[]): CandidateGroups {
  return {
    direct: candidates.filter((candidate) => candidate.group === 'direct'),
    alternative: candidates.filter((candidate) => candidate.group === 'alternative'),
    'not-relevant': candidates.filter((candidate) => candidate.group === 'not-relevant'),
  }
}

/** The ticked companies in list order. */
export function pickedCandidates(
  candidates: readonly CompetitorCandidate[],
  picks: ReadonlySet<string>,
): CompetitorCandidate[] {
  return candidates.filter((candidate) => picks.has(candidateDomainKey(candidate.domain)))
}

/** A list and the domain keys ticked on it. */
export interface SeededPicks {
  readonly candidates: readonly CompetitorCandidate[]
  readonly picks: ReadonlySet<string>
}

function listedCandidate(
  candidates: readonly CompetitorCandidate[],
  company: BrandCandidate,
): CompetitorCandidate | undefined {
  const key = candidateDomainKey(company.domain)
  return candidates.find((candidate) => candidateDomainKey(candidate.domain) === key)
}

/** The tracked companies as list rows, in tracked order: the listed row when the list holds the company, else a direct row. */
export function trackedCandidates(
  candidates: readonly CompetitorCandidate[],
  tracked: readonly BrandCandidate[],
): CompetitorCandidate[] {
  return tracked.map((company) => listedCandidate(candidates, company) ?? directRow(company))
}

/** The list a discovery opens with and its ticks: tracked companies up to `limit`, added to the direct group when missing, else the first direct companies. */
export function seedPicks(
  candidates: readonly CompetitorCandidate[],
  tracked: readonly BrandCandidate[],
  limit: number,
): SeededPicks {
  const kept = tracked.slice(0, limit)
  if (kept.length === 0) return { candidates, picks: defaultPicks(candidates, limit) }
  return {
    candidates: kept.reduce<readonly CompetitorCandidate[]>(
      (list, company) =>
        listedCandidate(list, company) ? list : withTrailingDirectCandidate(list, company),
      candidates,
    ),
    picks: new Set(kept.map((company) => candidateDomainKey(company.domain))),
  }
}
